import { shiftDay, startOfWeekDay } from "./day-string";
import { moment } from "./moment";

/** One cell of a month grid. */
export interface CalendarMonthDay {
	/** The stored `YYYY-MM-DD` string the cell stands for. */
	date: string;
	/** Whether the day belongs to the month before or after the one being drawn. */
	isOutsideMonth: boolean;
}

/** One row of a month grid, Monday to Sunday. */
export interface CalendarMonthWeek {
	/** The row's Monday. */
	start: string;
	/** The ISO week number of the row. */
	weekNumber: number;
	days: CalendarMonthDay[];
}

/** Rows a month grid always has, whatever the month's length. */
export const CALENDAR_MONTH_WEEKS = 6;
const DAYS_PER_WEEK = 7;

/**
 * The first day of the month a stored date lies in, moved by whole months.
 *
 * @param date - Any stored `YYYY-MM-DD` day of the starting month.
 * @param months - How many months to move; negative is earlier, zero is the month itself.
 * @returns The `YYYY-MM-01` of the target month.
 */
export function shiftMonth(date: string, months: number): string {
	const year = Number(date.slice(0, 4));
	const monthIndex = Number(date.slice(5, 7)) - 1 + months;
	return new Date(Date.UTC(year, monthIndex, 1)).toISOString().slice(0, 10);
}

/**
 * The grid a calendar draws for the month that holds a stored date: six Monday-first rows of seven
 * days, each row carrying its ISO week number and each day a flag when it belongs to a neighbouring
 * month.
 *
 * Six rows always, because the native date sheet is sized by its content — a grid that grew from
 * five rows to six as the month changed would move the sheet under the finger.
 *
 * @param date - Any stored `YYYY-MM-DD` day of the month to draw.
 * @returns The rows, the first holding the Monday on or before the 1st.
 */
export function buildCalendarMonth(date: string): CalendarMonthWeek[] {
	const month = date.slice(0, 7);
	const gridStart = startOfWeekDay(`${month}-01`);
	return Array.from({ length: CALENDAR_MONTH_WEEKS }, (_, weekIndex) => {
		const start = shiftDay(gridStart, weekIndex * DAYS_PER_WEEK);
		return {
			start,
			weekNumber: moment(start, "YYYY-MM-DD").isoWeek(),
			days: Array.from({ length: DAYS_PER_WEEK }, (_, dayIndex) => {
				const day = shiftDay(start, dayIndex);
				return { date: day, isOutsideMonth: !day.startsWith(month) };
			}),
		};
	});
}
